export interface PendingAccountUpdateInfo {
  userId: string;
  challengeId: string;
  targetEmail: string;
  savedAt: number;
}

const STORAGE_KEY = 'pendingAccountUpdateVerification';
const TTL_MS = 30 * 60 * 1000; // 30 minutes

type NullableStorage = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'> | null;

const getSessionStorage = (): NullableStorage => {
  if (typeof window === 'undefined') {
    return null;
  }
  try {
    return window.sessionStorage;
  } catch (err) {
    return null;
  }
};

export const savePendingAccountUpdate = (
  userId: string,
  challenge: { id: string; targetEmail: string },
) => {
  const storage = getSessionStorage();
  if (!storage) {
    return;
  }
  const payload: PendingAccountUpdateInfo = {
    savedAt: Date.now(),
    userId,
    challengeId: challenge.id,
    targetEmail: challenge.targetEmail,
  };
  storage.setItem(STORAGE_KEY, JSON.stringify(payload));
};

export const getPendingAccountUpdate = (): PendingAccountUpdateInfo | null => {
  const storage = getSessionStorage();
  if (!storage) {
    return null;
  }
  const raw = storage.getItem(STORAGE_KEY);
  if (!raw) {
    return null;
  }
  try {
    const parsed = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) {
      storage.removeItem(STORAGE_KEY);
      return null;
    }
    const savedAt = typeof parsed.savedAt === 'number' ? parsed.savedAt : NaN;
    const userId = typeof parsed.userId === 'string' ? parsed.userId : '';
    const challengeId = typeof parsed.challengeId === 'string' ? parsed.challengeId : '';
    const targetEmail = typeof parsed.targetEmail === 'string' ? parsed.targetEmail : '';
    if (!Number.isFinite(savedAt) || savedAt <= 0 || !userId || !challengeId || !targetEmail) {
      storage.removeItem(STORAGE_KEY);
      return null;
    }
    if (Date.now() - savedAt > TTL_MS) {
      storage.removeItem(STORAGE_KEY);
      return null;
    }
    return { savedAt, userId, challengeId, targetEmail };
  } catch (err) {
    storage.removeItem(STORAGE_KEY);
    return null;
  }
};

export const clearPendingAccountUpdate = () => {
  const storage = getSessionStorage();
  if (!storage) {
    return;
  }
  storage.removeItem(STORAGE_KEY);
};
